import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../api';
import '../components/Blog.css';
import '../components/MediumBlog.css';

export default function PostDetailPage({ slug, locale = 'ml' }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [cName, setCName] = useState('');
  const [cContent, setCContent] = useState('');
  const [cSubmitting, setCSubmitting] = useState(false);

  const labels = locale === 'en'
    ? { article: 'Article', back: 'Back', all: 'All Posts', cta: 'View Now', share: 'Share' }
    : { article: '\u0d32\u0d47\u0d16\u0d28\u0d02', back: '\u0d2a\u0d3f\u0d28\u0d4d\u0d28\u0d3f\u0d32\u0d47\u0d15\u0d4d\u0d15\u0d4d', all: '\u0d0e\u0d32\u0d4d\u0d32\u0d3e \u0d2a\u0d4b\u0d38\u0d4d\u0d31\u0d41\u0d15\u0d33\u0d41\u0d02', cta: '\u0d07\u0d2a\u0d4d\u0d2a\u0d4b\u0d33\u0d4d \u0d15\u0d3e\u0d23\u0d41\u0d15', share: '\u0d2a\u0d19\u0d4d\u0d15\u0d3f\u0d1f\u0d41\u0d15' };

  useEffect(() => {
    if (!slug) return;
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await apiGet(`/api/posts/${encodeURIComponent(slug)}`, { locale });
        if (!ignore) {
          setPost(data || null);
          // Update meta tags for social sharing
          if (data) {
            updateMetaTags(data);
          }
        }
      } catch (_) {
        if (!ignore) setError('Not found');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [slug, locale]);

  // Load comments when post is loaded
  useEffect(() => {
    if (!post?.id) return;
    let ignore = false;
    (async () => {
      try {
        setCommentsLoading(true);
        const list = await apiGet(`/api/posts/${post.id}/comments`);
        if (!ignore && Array.isArray(list)) setComments(list);
      } catch (_) { }
      finally { if (!ignore) setCommentsLoading(false); }
    })();
    return () => { ignore = true; };
  }, [post?.id]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setRecentLoading(true);
        // Fetch more items and only non-ad posts to ensure we can fill 3 suggestions
        const resp = await apiGet('/api/posts', { locale, isAd: 'false', limit: 50 });
        const arr = Array.isArray(resp?.items) ? resp.items : [];
        // Filter out current post, ensure slug exists, and deduplicate by slug/id
        const seen = new Set();
        const filtered = [];
        for (const p of arr) {
          if (p?.isAd) continue;
          if (!p?.slug) continue;
          if (p.slug === slug) continue;
          const key = (p.slug && String(p.slug)) || `id-${p.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          filtered.push(p);
          if (filtered.length >= 3) break;
        }
        const mapped = filtered.map((p) => ({ id: p.id, title: p.title, slug: p.slug, imageUrl: p.imageUrl }));
        if (!ignore) setRecent(mapped);
      } catch (_) { }
      finally { if (!ignore) setRecentLoading(false); }
    })();
    return () => { ignore = true; };
  }, [slug, locale]);

  const updateMetaTags = (post) => {
    const url = window.location.href;
    const title = post.metaTitle || post.title || 'Purayida Krishi';
    const description = post.metaDescription || post.excerpt || 'Sustainable Agriculture & Organic Farming';
    const image = post.imageUrl || `${window.location.origin}/purayida-krishi-logo-file.png`;

    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMeta('description', description);
    updateMeta('og:title', title);
    updateMeta('og:description', description);
    updateMeta('og:image', image);
    updateMeta('og:url', url);
    updateMeta('og:type', 'article');
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', image);
  };

  const sharePost = (platform) => {
    if (!post) return;
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post.title || '');
    const text = encodeURIComponent(post.excerpt || post.title || '');

    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${url}&text=${title}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(window.location.href);
        alert(locale === 'en' ? 'Link copied to clipboard!' : 'ലിങ്ക് കോപ്പി ചെയ്തു!');
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <section style={{ paddingTop: '24vh', background: '#fff', minHeight: '100vh' }}>
      <div className="medium-post-container">
        <button className="medium-back-button" onClick={() => { window.history.length > 1 ? window.history.back() : (window.location.hash = locale === 'en' ? '#/blog/en' : '#/blog/ml'); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {labels.back}
        </button>

        {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}

        {(post || loading) && (
          <div className="medium-layout">
            <div className="medium-main">
              {post && (
                <article>
                  <div className="medium-post-header">
                    <h1 className="medium-post-title">{post.title}</h1>
                    {post.excerpt && <p className="medium-post-subtitle">{post.excerpt}</p>}
                    <div className="medium-post-meta">
                      {post.date && <span className="medium-post-date">{post.date}</span>}
                      {post.category?.slug && <span className="medium-post-category">{post.category.slug}</span>}
                    </div>
                  </div>

                  {post.imageUrl && (
                    <img src={post.imageUrl} alt={post.title} className="medium-post-image" />
                  )}

                  {post.content ? (
                    <div className="medium-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
                  ) : (
                    <div className="medium-post-content">
                      <p>{post.excerpt || ''}</p>
                    </div>
                  )}

                  {post.isAd && post.ctaUrl && (
                    <div style={{ marginTop: 32 }}>
                      <a href={post.ctaUrl} target="_blank" rel="noreferrer" className="medium-back-button" style={{ background: '#2d5016', color: '#fff', borderColor: '#2d5016' }}>
                        {post.ctaText || labels.cta}
                      </a>
                    </div>
                  )}

                  {/* Share Buttons */}
                  <div style={{ marginTop: 48, paddingTop: 32, borderTop: '1px solid #e6e6e6' }}>
                    <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#242424' }}>{labels.share}</h4>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <button onClick={() => sharePost('facebook')} className="share-button share-facebook" title="Share on Facebook">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </button>
                      <button onClick={() => sharePost('twitter')} className="share-button share-twitter" title="Share on Twitter">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                      </button>
                      <button onClick={() => sharePost('whatsapp')} className="share-button share-whatsapp" title="Share on WhatsApp">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>
                      <button onClick={() => sharePost('telegram')} className="share-button share-telegram" title="Share on Telegram">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                        </svg>
                      </button>
                      <button onClick={() => sharePost('linkedin')} className="share-button share-linkedin" title="Share on LinkedIn">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </button>
                      <button onClick={() => sharePost('copy')} className="share-button share-copy" title="Copy Link">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Comments */}
                  <div style={{ marginTop: 48, paddingTop: 32, borderTop: '1px solid #e6e6e6' }}>
                    <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#242424' }}>{locale === 'en' ? 'Comments' : '\u0d2e\u0d24\u0d4d\u0d38\u0d30\u0d19\u0d4d\u0d19\u0d33\u0d4d'}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {commentsLoading && <div style={{ color: '#6b6b6b' }}>{locale === 'en' ? 'Loading comments…' : '\u0d2e\u0d24\u0d4d\u0d38\u0d30\u0d19\u0d4d\u0d19\u0d33\u0d4d \u0d32\u0d4b\u0d21\u0d4d \u0d1a\u0d46\u0d2f\u0d4d\u0d2f\u0d41\u0d28\u0d4d\u0d28\u0d41'} </div>}
                      {!commentsLoading && comments.length === 0 && <div style={{ color: '#6b6b6b' }}>{locale === 'en' ? 'Be the first to comment.' : 'ആദ്യ അഭിപ്രായം'} </div>}
                      {comments.map((cm) => (
                        <div key={cm.id} style={{ padding: 12, border: '1px solid #e6e6e6', borderRadius: 8 }}>
                          <div style={{ fontWeight: 600 }}>{cm.name}</div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{cm.content}</div>
                          <div style={{ fontSize: 12, color: '#6b6b6b', marginTop: 4 }}>{new Date(cm.createdAt).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>

                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!post?.id) return;
                        const name = cName.trim();
                        const content = cContent.trim();
                        if (!name || !content) return;
                        try {
                          setCSubmitting(true);
                          await apiPost(`/api/posts/${post.id}/comments`, { name, content });
                          setCName('');
                          setCContent('');
                          // reload comments
                          const list = await apiGet(`/api/posts/${post.id}/comments`);
                          setComments(Array.isArray(list) ? list : []);
                        } catch (_) {
                          alert(locale === 'en' ? 'Failed to submit comment' : '\u0d2e\u0d24\u0d4d\u0d38\u0d30\u0d02 \u0d38\u0d2e\u0d30\u0d4d\u0d2a\u0d4d\u0d2a\u0d3f\u0d15\u0d4d\u0d15\u0d41\u0d28\u0d4d\u0d28\u0d41');
                        } finally {
                          setCSubmitting(false);
                        }
                      }}
                      style={{ marginTop: 16, display: 'grid', gap: 12 }}
                    >
                      <input
                        className="input"
                        placeholder={locale === 'en' ? 'Your name' : 'നിങ്ങളുടെ പേര്'}
                        value={cName}
                        onChange={(e) => setCName(e.target.value)}
                        required
                      />
                      <textarea
                        className="input"
                        placeholder={locale === 'en' ? 'Your comment' : 'നിങ്ങളുടെ അഭിപ്രായം'}
                        rows={4}
                        value={cContent}
                        onChange={(e) => setCContent(e.target.value)}
                        required
                      />
                      <button className="medium-back-button" type="submit" disabled={cSubmitting} style={{ alignSelf: 'start' }}>
                        {cSubmitting ? (locale === 'en' ? 'Submitting…' : '\u0d38\u0d2e\u0d30\u0d4d\u0d2a\u0d4d\u0d2a\u0d3f\u0d15\u0d4d\u0d15\u0d41\u0d28\u0d4d\u0d28\u0d41…') : (locale === 'en' ? 'Post Comment' : '\u0d2e\u0d24\u0d4d\u0d38\u0d30\u0d02 \u0d05\u0d2f\u0d15\u0d4d\u0d15\u0d41')}
                      </button>
                    </form>
                  </div>

                  <div className="medium-post-footer" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {(recentLoading || recent.length > 0) && (
                      <div style={{ marginBottom: 16 }}>
                        <h4 style={{ fontSize: 18, fontWeight: 600, margin: 0, marginBottom: 12, color: '#242424' }}>
                          {locale === 'en' ? 'Recent Posts' : '\u0d2a\u0d41\u0d24\u0d3f\u0d2f\u0d24\u0d4d\u0d24\u0d28\u0d4d\u0d28\u0d46 \u0d2a\u0d4b\u0d38\u0d4d\u0d31\u0d41\u0d15\u0d7e'}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
                          {recentLoading && Array.from({ length: 3 }).map((_, i) => (
                            <div key={`s-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px', border: '1px solid #e6e6e6', borderRadius: 8, width: 220 }}>
                              <div style={{ width: 200, height: 200, background: '#f0f0f0', borderRadius: 6, alignSelf: 'center' }} />
                              <div>
                                <div style={{ height: 10, background: '#f0f0f0', borderRadius: 4, width: '90%', marginBottom: 6 }} />
                                <div style={{ height: 10, background: '#f0f0f0', borderRadius: 4, width: '70%' }} />
                              </div>
                            </div>
                          ))}
                          {!recentLoading && recent.map((r) => (
                            <button
                              key={r.id}
                              onClick={() => { window.scrollTo(0, 0); window.location.hash = `#/blog/${locale === 'en' ? 'en' : 'ml'}/${encodeURIComponent(r.slug)}`; }}
                              className="medium-back-button"
                              style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, width: 220, alignItems: 'stretch' }}
                              title={r.title || ''}
                            >
                              {r.imageUrl ? (
                                <img
                                  src={r.imageUrl}
                                  alt={r.title || ''}
                                  width={200}
                                  height={200}
                                  style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 6, alignSelf: 'center' }}
                                  onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/200x200/eeeeee/9ca3af?text=%20'; }}
                                />
                              ) : (
                                <div style={{ width: 200, height: 200, background: '#f0f0f0', borderRadius: 6, alignSelf: 'center' }} />
                              )}
                              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <button className="medium-back-button" onClick={() => { window.location.hash = locale === 'en' ? '#/blog/en' : '#/blog/ml'; }}>
                      {labels.all}
                    </button>
                  </div>
                </article>
              )}

              {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b6b6b' }}>Loading…</div>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
